import React, { useMemo } from 'react';

import { useTranslation } from '@modernloop/i18n';
import { extractTextFromHTML } from '@modernloop/shared/utils';
import { Box, Divider, Stack, Typography } from '@mui/material';

import Link from 'src/components/Link';
import { createSxProps } from 'src/components/types';

import { replaceLinksWithAnchorTags } from 'src/utils/strings';

import ActivityLogContent from '../../Helpers/ActivityLogContent';

const useSxProps = () => {
  const sxProps = useMemo(
    () => ({
      labelExpanded: {
        overflowX: 'auto',
        maxWidth: '558px',
      },

      metadata: {
        backgroundColor: 'background.subtle',
        px: 1.75,
        py: 0.75,
      },

      metadataLabel: {
        color: 'text.secondary',
        fontWeight: 600,
        fontSize: (theme) => theme.typography.caption.fontSize,
        minWidth: '52px',
      },

      metadataValue: {
        color: 'text.primary',
        fontWeight: 500,
        fontSize: (theme) => theme.typography.body2.fontSize,
        wordBreak: 'break-word',
      },

      body: {
        whiteSpace: 'normal',
        color: 'text.primary',
        fontSize: (theme) => theme.typography.body1.fontSize,
        lineHeight: 1.55,
        '& p': {
          margin: 0,
        },
        '& p + p': {
          marginTop: '12px',
        },
        '& div': {
          maxWidth: '100%',
        },
        '& ul': {
          margin: '8px 0 0 28px',
        },
        '& ol': {
          margin: '8px 0 0 28px',
        },
        '& a': {
          wordBreak: 'break-word',
        },
      },

      bodyPreview: {
        color: 'text.primary',
        lineHeight: 1.6,
        whiteSpace: 'pre-line',
      },

      bodySection: {
        px: 1.75,
        pt: 0.375,
      },

      divider: {
        borderColor: 'borderState.strong',
      },

      expandLink: {
        mt: 0.25,
        display: 'flex',
        justifyContent: 'flex-end',
        px: 1.75,
        pb: 0,
      },
    }),
    []
  );

  return createSxProps(sxProps);
};

interface Props {
  emailAddress: string;
  emailAddressLabel: string;
  ccAddresses: string[];
  subject: string;
  body: string;
}

const ActivityLogFeedEmail = ({ emailAddress, emailAddressLabel, ccAddresses, subject, body }: Props) => {
  const sxProps = useSxProps();
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = React.useState(false);
  const cc = ccAddresses.join(', ');

  const getMetadataRow = (label: string, value: string) => (
    <Stack direction="row" spacing={1} alignItems="flex-start">
      <Typography sx={sxProps.metadataLabel}>{label}</Typography>
      <Typography sx={sxProps.metadataValue}>{value}</Typography>
    </Stack>
  );

  const getLinkButton = (label: string, onClick: () => void) => {
    return (
      <Box sx={sxProps.expandLink}>
        {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
        <Link
          dataTestId="activity-log-email-show-more-button"
          component="button"
          href=""
          onClick={onClick}
          color="info"
          variant="caption"
          fontWeight={500}
        >
          {label}
        </Link>
      </Box>
    );
  };

  const metadataSection = (
    <Stack direction="column" spacing={0.75} sx={sxProps.metadata}>
      {emailAddress ? getMetadataRow(emailAddressLabel, emailAddress) : null}
      {cc
        ? getMetadataRow(t('hiring-dashboard-candidates:candidates.activityLog.cc', { defaultValue: 'CC:' }), cc)
        : null}
      {getMetadataRow(
        t('hiring-dashboard-candidates:candidates.activityLog.subject', { defaultValue: 'Subject:' }),
        subject
      )}
    </Stack>
  );

  const expanded = (
    <Stack direction="column" spacing={0.875}>
      {metadataSection}
      <Divider sx={sxProps.divider} />
      <Box sx={sxProps.bodySection}>
        <Typography component="div" sx={{ ...sxProps.labelExpanded, ...sxProps.body }}>
          <Box component="span" dangerouslySetInnerHTML={{ __html: replaceLinksWithAnchorTags(body).text }} />
        </Typography>
      </Box>
      {getLinkButton(
        t('hiring-dashboard-candidates:candidates.activityLog.showLess', { defaultValue: 'Show less' }),
        () => {
          setIsExpanded(false);
        }
      )}
    </Stack>
  );

  const collapsed = (
    <Stack direction="column" spacing={0.875}>
      {metadataSection}
      <Divider sx={sxProps.divider} />
      <Box sx={sxProps.bodySection}>
        <Typography
          overflow="hidden"
          display="-webkit-box"
          textOverflow="ellipsis"
          sx={{
            ...sxProps.bodyPreview,
            WebkitLineClamp: 4,
            WebkitBoxOrient: 'vertical',
          }}
        >
          {extractTextFromHTML(body)}
        </Typography>
      </Box>
      {getLinkButton(
        t('hiring-dashboard-candidates:candidates.activityLog.showMore', { defaultValue: 'Show more' }),
        () => {
          setIsExpanded(true);
        }
      )}
    </Stack>
  );

  return (
    <ActivityLogContent
      fullWidth
      sx={{
        borderRadius: '10px',
        px: 0,
        py: 1.5,
        borderColor: 'borderState.strong',
        overflow: 'hidden',
      }}
      content={isExpanded ? expanded : collapsed}
    />
  );
};

export default ActivityLogFeedEmail;
